import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Box, Typography } from '@material-ui/core';

import { useBackendContext } from '@omq/shared';

import { BASE_URL, EMAIL_VALIDATION_PATTERN } from '@admin/conf';

import { LoginAPI } from '@admin/shared/api/login';
import { useDialogContext } from '@admin/shared/context/dialog';

import { Link } from '@admin/shared/components/router/link';
import { Headline } from '@admin/shared/components/typography/headline';
import { Form, FormRow } from '@admin/shared/components/forms/form';
import { TextField } from '@admin/shared/components/inputs/text-field/text-field';
import { PasswordInput } from '@admin/shared/components/inputs/text-field/password';
import { UnauthorizedIcon } from '@admin/shared/components/icons/custom/unauthorized';
import { LoadingIndicator } from '@admin/shared/components/progress/loading-indicator';
import { LinkButton } from '@admin/shared/components/buttons/link-button/link-button';
import { DialogSizes, useDialogStyles } from '@admin/shared/components/dialogs/dialog';
import type { ValidationState } from '@admin/shared/util/validation';
import { Validation } from '@admin/shared/util/validation';
import { LOGIN_ROUTES } from '@admin/login/login';

declare global {
  interface Window {
    useAdminContext: boolean;
    versionName: string;
  }
}

export const DEFAULT_REDIRECT_LOCATION = `/${BASE_URL}/desk`;
const DEFAULT_ADMIN_REDIRECT_LOCATION = `/${BASE_URL}/admin/tenants`;

const { useAdminContext } = window;

/**
 * Return URL to where the user should be redirected to after login.
 * If not available, redirect to desk.
 *
 * @returns {string}
 */
const getRedirectURL = () => {
  // there is no desk yet, or any other modules
  // for the superuser UI, therefore we just redirect directly
  // to the only available module
  if (useAdminContext) {
    return DEFAULT_ADMIN_REDIRECT_LOCATION;
  }

  // get url params
  const params = new URLSearchParams(window.location.search);
  const origin = params.get('origin');

  // check if origin was passed
  if (origin != null && origin !== '') {
    return origin;
  }

  // default redirect
  return DEFAULT_REDIRECT_LOCATION;
};

// config of form validation
const validationConfig = {
  email: {
    isRequired: true,
    pattern: EMAIL_VALIDATION_PATTERN,
  },

  password: {
    isRequired: true,
  },
};

/**
 * Renders login form.
 *
 * @author Florian Walch
 * @since 9.7
 *
 * @returns {JSX.Element}
 */
export const LoginForm = (): JSX.Element => {
  const dialogStyles = useDialogStyles();
  const { t } = useTranslation(['login']);

  // set up states
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [validationState, setValidationState] = useState<ValidationState>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const backend = useBackendContext();
  const { showDialog } = useDialogContext();

  // validate form input
  const handleFormValidation = () => {
    const error = Validation.validateRecordWithConfig(
      {
        email: username,
        password,
      },
      validationConfig,
    );

    setValidationState(error);

    return Object.keys(error).length === 0;
  };

  // handle submit, perform login
  const handleUserLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      setIsLoading(true);

      // send login request
      const user = await LoginAPI.login(backend, username, password, useAdminContext).finally(
        () => {
          setIsLoading(false);
        },
      );

      if (user != null) {
        (window as Window).location = getRedirectURL();
      }
    } catch (ex) {
      // check status code of login failure
      const { status } = ex.response;

      // decide which error message to display
      const errorMessage =
        status === 401
          ? t('dialogs.loginFailed.incorrectCredentials')
          : t('dialogs.loginFailed.insufficientAuthorization');

      // show error
      showDialog({
        size: DialogSizes.SMALL,
        children: (
          <Box display="flex" flexDirection="column" alignItems="center">
            <UnauthorizedIcon className={dialogStyles.icon} />
            <p className={dialogStyles.headline}>{t('dialogs.loginFailed.title')}</p>
            <p className={dialogStyles.text}>{errorMessage}</p>
          </Box>
        ),
        actions: [
          {
            label: t('login:buttons.ok'),
            color: 'primary',
            autoFocus: true,
          },
        ],
      });
    }
  };

  // render form
  return (
    <Box>
      <Form onSubmit={handleUserLogin} customValidation={handleFormValidation}>
        <FormRow margin="large">
          <Headline size="large">{t('login:headlines.login')}</Headline>
        </FormRow>

        <FormRow>
          <TextField
            placeholder={t('login:inputs.mail')}
            required={true}
            autoComplete="username"
            type="email"
            validationConfig={validationConfig.email}
            errorMessage={validationState.email}
            value={username}
            onChange={(evt) => {
              setValidationState({});
              setUsername(evt.target.value);
            }}
          />
        </FormRow>

        <FormRow margin="large">
          <PasswordInput
            placeholder={t('login:inputs.password')}
            value={password}
            validationConfig={validationConfig.password}
            errorMessage={validationState.password}
            onChange={(evt) => {
              setValidationState({});
              setPassword(evt.target.value);
            }}
          />
        </FormRow>

        <FormRow>
          <Button variant="contained" color="primary" type="submit">
            {t('login:buttons.login')}
          </Button>

          <Box display="inline-block" ml={11}>
            <Link to={LOGIN_ROUTES.FORGOT_PASSWORD}>
              <LinkButton>{t('login:links.forgotPassword')}</LinkButton>
            </Link>
          </Box>
        </FormRow>
      </Form>

      <Box my={5} color="grey.600">
        <Typography variant="body1" color="textSecondary" align="right">
          {window.versionName}
        </Typography>
      </Box>

      {isLoading && <LoadingIndicator />}
    </Box>
  );
};
